// pages/about.ts — about page. Trustee section deliberately omitted
// (matches frontend2 — see Public.jsx :: AboutPage and readme §Q2).

import type { Page, PageContext } from '../router';
import { html } from '../util';

const PROMISES: Array<[string, string]> = [
  ['A human reads your application.',
   "Not a screener, not an algorithm — a trustee with a name. We won't pretend otherwise."],
  ['We say why if we say no.',
   'A rejection comes with feedback, so you know whether to revise and reapply or seek funding elsewhere.'],
  ['The money arrives quickly.',
   "Approved grants are wired within ten business days of the decision. We don't drip-feed."],
  ['We show up afterwards.',
   'We come to ribbon cuttings, soft launches, and pantry shifts. Not to inspect — to participate.'],
];

const CONTACT: Array<[string, string]> = [
  ['Telephone',    '214 · 378 · 9131'],
  ['Office hours', 'Monday – Friday\n9 am – 5 pm Eastern'],
];

function renderPromises(): string {
  return PROMISES.map(([t, b], i) => html`
    <div class="d-flex flex-column" style="gap: 12px;">
      <div class="t-small" style="color: var(--primary); font-weight: 500;">${String(i + 1).padStart(2, '0')}</div>
      <h3 class="t-h3 serif" style="margin: 0;">${t}</h3>
      <p class="t-body" style="margin: 0;">${b}</p>
    </div>
  `).join('');
}

function renderContactGrid(): string {
  return CONTACT.map(([k, v]) => html`
    <div class="card" style="padding: 28px 28px;">
      <div class="label">${k}</div>
      <div class="serif" style="font-size: 22px; line-height: 1.35; margin-top: 12px; white-space: pre-line;">${v}</div>
    </div>
  `).join('');
}

const AboutPage: Page = {
  render(_ctx: PageContext): string {
    return html`
      <section style="padding: 80px 0 64px;">
        <div class="wrap">
          <div style="max-width: 880px;">
            <div class="eyebrow">Who we are</div>
            <h1 class="t-h1" style="margin: 16px 0 0;">
              About The Bray Family Foundation.
            </h1>
            <p class="t-lede" style="margin-top: 32px;">
              A small, all-volunteer family foundation supporting community-rooted nonprofits in our region. Founded in 1995, run from a kitchen table.
            </p>
          </div>
        </div>
      </section>

      <section style="padding: 48px 0 96px;">
        <div class="wrap landing-mission-grid">
          <img src="assets/img/about-archival-2.jpg"
               alt="Three generations sharing a moment at the community garden in 1995"
               class="brand-img"
               style="aspect-ratio: 11/6; width: 100%;"/>
          <div class="d-flex flex-column" style="gap: 24px;">
            <p class="t-body" style="font-size: 18px;">
              Founded in 1995, The Bray Family Foundation operates on the belief that community organizations closest to a problem are best positioned to solve it. Our grants are modest by design — they go to organizations larger funders often overlook.
            </p>
            <p class="t-body" style="font-size: 18px;">
              Our all-volunteer review board reads every application personally. We ask questions, offer feedback, and stay connected with grantees long after the check clears. We're not just funders — we're community partners.
            </p>
            <p class="t-body" style="font-size: 18px;">
              The first cheque, in 1995, was $500 for a community garden three blocks from the house. It is still growing.
            </p>
          </div>
        </div>
      </section>

      <section class="divide divide-soft">
        <div class="wrap">
          <div class="eyebrow">Our promises</div>
          <h2 class="t-h2" style="margin: 16px 0 64px; max-width: 680px;">
            What you can count on from us.
          </h2>
          <div class="about-promises-grid">
            ${{ __raw: renderPromises() }}
          </div>
        </div>
      </section>

      <section class="divide">
        <div class="wrap landing-mission-grid">
          <div>
            <div class="eyebrow">Reach us</div>
            <h2 class="t-h2" style="margin: 16px 0 0;">We're easy to find.</h2>
            <p class="t-body" style="margin-top: 24px; max-width: 380px;">
              For questions before applying, an email is fastest. We try to answer within two business days.
            </p>
          </div>
          <div class="about-contact-grid">
            ${{ __raw: renderContactGrid() }}
          </div>
        </div>
      </section>
    `;
  },
};

export default AboutPage;
